import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Dialog,
  InputBase,
  LinearProgress,
  Typography,
  alpha,
} from "@mui/material";
import {
  KeyboardReturnRounded,
  NorthEastRounded,
  SearchRounded,
} from "@mui/icons-material";
import color from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTerminology } from "../../Core/useTerminology";
import { useDebouncedCallback } from "../../Hooks/useDebouncedCallback";
import { DashboardItem, DashboardItemType } from "../../Models/dashboard";
import {
  SEARCH_MIN_LENGTH,
  useDashboardSearch,
  useMarkModuleOpened,
  useModulePreferences,
} from "../../Query/useDashboard";
import { DashboardModuleDefinition, openModule } from "./dashboardModules";
import {
  ItemIcon,
  StatusPill,
  itemPath,
  itemStatus,
  makeTimeAgo,
  parseServerTime,
} from "./dashboardItems";
import { useDashboardColors } from "./dashboardColors";

type SearchResult =
  | { kind: "module"; key: string; module: DashboardModuleDefinition }
  | { kind: "item"; key: string; item: DashboardItem };

interface ResultGroup {
  key: string;
  label: string;
  results: SearchResult[];
}

const MAX_MODULE_RESULTS = 4;
const MAX_JUMP_TO = 6;

const normalize = (value: string) =>
  value
    .toLocaleLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const words = (value: string) =>
  normalize(value)
    .split(/[\s,.;:()/&+-]+/)
    .filter(Boolean);

/**
 * 2 when every query word starts a word of the title, 1 when it does so in
 * title and description together, 0 otherwise. Prefix matching keeps short
 * queries from hitting the middle of unrelated words ("as" in "tasks").
 */
const moduleScore = (module: DashboardModuleDefinition, query: string) => {
  const tokens = words(query);
  const titleWords = words(module.title);
  const allWords = [...titleWords, ...words(module.description)];
  const covers = (pool: string[]) =>
    tokens.every((token) => pool.some((word) => word.startsWith(token)));

  if (covers(titleWords)) return 2;
  if (covers(allWords)) return 1;
  return 0;
};

/** Wraps the first match of `query` in `text` in a <mark>. */
const Highlight: React.FC<{ text: string; query: string }> = ({
  text,
  query,
}) => {
  // Lower-casing keeps string indexes aligned with `text`; a match that only
  // exists without accents is found by the filter but simply not highlighted.
  const haystack = text.toLocaleLowerCase();
  const needle = query.trim().toLocaleLowerCase();
  // Prefer a match at the start of a word, as module matching does.
  const isWordStart = (at: number) =>
    at === 0 || /[\s,.;:()/&+-]/.test(haystack[at - 1]);
  let index = haystack.indexOf(needle);
  let candidate = index;
  while (candidate >= 0 && !isWordStart(candidate)) {
    candidate = haystack.indexOf(needle, candidate + 1);
  }
  if (candidate >= 0) index = candidate;
  if (!needle || index < 0) return <>{text}</>;
  const end = index + needle.length;
  return (
    <>
      {text.slice(0, index)}
      <Box
        component="mark"
        sx={{
          backgroundColor: alpha(color.orange500, 0.18),
          color: "inherit",
          borderRadius: "3px",
          px: "1px",
        }}
      >
        {text.slice(index, end)}
      </Box>
      {text.slice(end)}
    </>
  );
};

interface GlobalSearchDialogProps {
  open: boolean;
  onClose: () => void;
  modules: DashboardModuleDefinition[];
}

const GlobalSearchDialog: React.FC<GlobalSearchDialogProps> = ({
  open,
  onClose,
  modules,
}) => {
  const { t, i18n } = useTranslation("home");
  const navigate = useNavigate();
  const terminology = useTerminology();
  const { colors: workspace } = useWorkspaceColors();
  const { accent, accentIcon, accentSoft, onAccent } = useDashboardColors();
  const { mutate: markOpened } = useMarkModuleOpened();
  const { data: preferences = [] } = useModulePreferences();
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const timeAgo = makeTimeAgo(t, i18n.language);

  const updateQuery = useDebouncedCallback((value: string) => {
    setQuery(value);
    setActiveIndex(0);
  }, 200);

  const trimmed = query.trim();
  const searching = trimmed.length >= SEARCH_MIN_LENGTH;
  const { data, isFetching } = useDashboardSearch(query);

  const typeLabel = (type: DashboardItemType) =>
    t(`hub.search.groups.${type}`, {
      entity: terminology.plural,
    });

  const accessibleModules = modules.filter((module) => module.hasAccess);
  const preferenceOf = (module: DashboardModuleDefinition) =>
    preferences.find((preference) => preference.module_key === module.key);
  // Without a query: favorites, then recently opened, then the usual order.
  const jumpRank = (module: DashboardModuleDefinition) => {
    const preference = preferenceOf(module);
    const opened = parseServerTime(preference?.last_opened_at)?.getTime() ?? 0;
    return (preference?.is_favorite ? 1 : 0) * 1e15 + opened;
  };

  const matchedModules = trimmed
    ? accessibleModules
        .map((module) => ({ module, score: moduleScore(module, trimmed) }))
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score)
        .map((entry) => entry.module)
        .slice(0, MAX_MODULE_RESULTS)
    : [...accessibleModules]
        .sort((a, b) => jumpRank(b) - jumpRank(a))
        .slice(0, MAX_JUMP_TO);

  const moduleGroup: ResultGroup = {
    key: "modules",
    label: trimmed ? t("hub.search.groups.modules") : t("hub.search.jumpTo"),
    results: matchedModules.map((module) => ({
      kind: "module",
      key: `module-${module.key}`,
      module,
    })),
  };

  // Results of an older query stay visible while the new one loads.
  const itemGroups: ResultGroup[] =
    searching && data
      ? data.groups.map((group) => ({
          key: group.type,
          label: typeLabel(group.type),
          results: group.items.map((item) => ({
            kind: "item",
            key: `${item.type}-${item.id}`,
            item,
          })),
        }))
      : [];

  const groups = [moduleGroup, ...itemGroups].filter(
    (group) => group.results.length > 0,
  );
  const flat = groups.flatMap((group) => group.results);
  const active = Math.min(activeIndex, Math.max(flat.length - 1, 0));

  const reset = () => {
    setInput("");
    setQuery("");
    setActiveIndex(0);
  };

  const close = () => {
    onClose();
    reset();
  };

  const choose = (result: SearchResult) => {
    close();
    if (result.kind === "module") {
      if (result.module.externalUrl) markOpened(result.module.key);
      openModule(result.module, navigate);
    } else {
      navigate(itemPath(result.item));
    }
  };

  const scrollIntoView = (index: number) => {
    const node = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${index}"]`,
    );
    node?.scrollIntoView({ block: "nearest" });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (flat.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      const next = (active + step + flat.length) % flat.length;
      setActiveIndex(next);
      scrollIntoView(next);
    } else if (event.key === "Enter" && flat[active]) {
      event.preventDefault();
      choose(flat[active]);
    }
  };

  const showNoResults =
    searching && !isFetching && data?.query === trimmed && groups.length === 0;

  let index = -1;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      aria-label={t("hub.search.open")}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
      PaperProps={{
        sx: {
          mt: { xs: 2, sm: "10vh" },
          mx: { xs: 2, sm: 4 },
          width: { xs: "calc(100% - 32px)", sm: "100%" },
          borderRadius: "16px",
          overflow: "hidden",
          maxHeight: { xs: "calc(100% - 32px)", sm: "75vh" },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          height: 56,
          borderBottom: `1px solid ${color.gray200}`,
        }}
      >
        <SearchRounded sx={{ color: accentIcon, fontSize: 22 }} />
        <InputBase
          autoFocus
          fullWidth
          value={input}
          onChange={(event) => {
            setInput(event.target.value);
            updateQuery(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={t("hub.search.placeholder")}
          inputProps={{
            "aria-label": t("hub.search.placeholder"),
            role: "combobox",
            "aria-expanded": flat.length > 0,
            "aria-controls": "dashboard-search-results",
            "aria-activedescendant": flat[active]
              ? `dashboard-search-${flat[active].key}`
              : undefined,
          }}
          sx={{ fontSize: 15 }}
        />
        <Box
          component="kbd"
          onClick={close}
          sx={{
            cursor: "pointer",
            px: 0.75,
            py: 0.25,
            borderRadius: "6px",
            border: `1px solid ${color.gray200}`,
            backgroundColor: color.gray50,
            fontSize: 11,
            fontFamily: "inherit",
            fontWeight: 600,
            color: color.gray500,
          }}
        >
          Esc
        </Box>
        {isFetching && searching && (
          <LinearProgress
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: -1,
              height: 2,
              backgroundColor: "transparent",
              "& .MuiLinearProgress-bar": {
                backgroundColor: accent,
              },
            }}
          />
        )}
      </Box>

      <Box
        ref={listRef}
        id="dashboard-search-results"
        role="listbox"
        sx={{
          overflowY: "auto",
          py: 1,
          opacity: isFetching && searching ? 0.7 : 1,
          transition: "opacity 0.15s ease",
        }}
      >
        {trimmed.length > 0 && !searching && (
          <Typography
            sx={{
              px: 2.5,
              py: 1,
              fontSize: 13,
              lineHeight: "18px",
              color: color.gray500,
            }}
          >
            {t("hub.search.minChars", { count: SEARCH_MIN_LENGTH })}
          </Typography>
        )}

        {showNoResults && (
          <Box sx={{ px: 2.5, py: 4, textAlign: "center" }}>
            <Typography
              sx={{ fontSize: 14, lineHeight: "20px", color: color.gray700 }}
            >
              {t("hub.search.noResults", { query: trimmed })}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 13,
                lineHeight: "18px",
                color: color.gray500,
              }}
            >
              {t("hub.search.noResultsHint")}
            </Typography>
          </Box>
        )}

        {groups.map((group) => (
          <Box
            key={group.key}
            role="group"
            aria-label={group.label}
            sx={{ mb: 0.5 }}
          >
            <Typography
              sx={{
                px: 2.5,
                pt: 1,
                pb: 0.5,
                fontSize: 11,
                lineHeight: "16px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: color.gray500,
              }}
            >
              {group.label}
            </Typography>
            {group.results.map((result) => {
              index += 1;
              const resultIndex = index;
              const selected = resultIndex === active;
              const isModule = result.kind === "module";
              const title = isModule ? result.module.title : result.item.title;
              const subtitle = isModule
                ? result.module.description
                : [result.item.subtitle, timeAgo(result.item.timestamp)]
                    .filter(Boolean)
                    .join(" · ");
              const status = isModule ? null : itemStatus(result.item, t);
              const ModuleGlyph = isModule ? result.module.icon : null;

              return (
                <Box
                  key={result.key}
                  id={`dashboard-search-${result.key}`}
                  data-index={resultIndex}
                  role="option"
                  aria-selected={selected}
                  onMouseMove={() => {
                    if (!selected) setActiveIndex(resultIndex);
                  }}
                  onClick={() => choose(result)}
                  sx={{
                    mx: 1,
                    px: 1.5,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    borderRadius: "10px",
                    cursor: "pointer",
                    backgroundColor: selected ? accentSoft : "transparent",
                    boxShadow: selected ? `inset 3px 0 0 ${accent}` : "none",
                  }}
                >
                  {ModuleGlyph ? (
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color:
                          result.kind === "module" && result.module.tone === 1
                            ? onAccent
                            : color.white,
                        backgroundColor:
                          result.kind === "module" && result.module.tone === 1
                            ? accent
                            : workspace.primary_dark,
                      }}
                    >
                      <ModuleGlyph sx={{ fontSize: 20 }} />
                    </Box>
                  ) : (
                    result.kind === "item" && <ItemIcon item={result.item} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14,
                        lineHeight: "20px",
                        fontWeight: 600,
                        color: color.gray900,
                      }}
                    >
                      <Highlight text={title} query={trimmed} />
                    </Typography>
                    {subtitle && (
                      <Typography
                        noWrap
                        sx={{
                          fontSize: 12,
                          lineHeight: "16px",
                          color: color.gray500,
                        }}
                      >
                        {subtitle}
                      </Typography>
                    )}
                  </Box>
                  {status && (
                    <StatusPill label={status.label} tone={status.tone} />
                  )}
                  {isModule && result.module.externalUrl ? (
                    <NorthEastRounded
                      sx={{ fontSize: 16, color: color.gray400 }}
                    />
                  ) : (
                    selected && (
                      <KeyboardReturnRounded
                        sx={{ fontSize: 16, color: color.gray400 }}
                      />
                    )
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          gap: 2,
          px: 2.5,
          py: 1.25,
          borderTop: `1px solid ${color.gray200}`,
          backgroundColor: color.gray50,
          fontSize: 12,
          color: color.gray500,
        }}
      >
        <span>↑ ↓ {t("hub.search.hintNavigate")}</span>
        <span>↵ {t("hub.search.hintOpen")}</span>
        <span>Esc {t("hub.search.hintClose")}</span>
        <Box component="span" sx={{ ml: "auto" }}>
          {t("hub.search.scope")}
        </Box>
      </Box>
    </Dialog>
  );
};

export default GlobalSearchDialog;
